// @flow

import "moment/locale/sv"

import { action, observable } from "mobx"
import moment from "moment"
import { createIntl } from "react-intl"
import UiStore from "shared/stores/UiStore"

import locales, { DEFAULT_LANGUAGE } from "../locales"
import type { Language } from "../types/IntlTypes"

const storedLanguage = UiStore.IntelLanguage || DEFAULT_LANGUAGE

class IntlStore {
  @observable locale = locales[storedLanguage] || locales[DEFAULT_LANGUAGE]
  _isFormatting: boolean = false

  intlInstance: {
    formatMessage: Function
  }

  constructor() {
    this.setInstance()
  }

  get messages(): any {
    return this.locale.messages
  }

  get currentLanguage(): Language {
    return this.locale.name
  }

  setInstance() {
    try {
      const selectedLocale = this.locale || locales[DEFAULT_LANGUAGE]

      if (!selectedLocale || !selectedLocale.messages) {
        // eslint-disable-next-line no-console
        console.error(
          "IntlStore: Invalid locale configuration:",
          selectedLocale
        )
        throw new Error("Invalid locale configuration")
      }

      moment.locale(selectedLocale.name)

      // Clear any existing instance to avoid stale state
      this.intlInstance = null

      // Create a fresh intl instance
      this.intlInstance = createIntl(
        {
          locale: selectedLocale.name,
          messages: selectedLocale.messages,
          defaultLocale: "sv" // Ensure we have a fallback
        },
        // Pass empty cache to avoid stale cached messages
        {}
      )

      // eslint-disable-next-line no-console
      console.log(
        `IntlStore: Successfully initialized with locale "${selectedLocale.name}"`
      )
      // eslint-disable-next-line no-console
      console.log(
        `IntlStore: Loaded ${
          Object.keys(selectedLocale.messages).length
        } messages`
      )
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("IntlStore: Failed to initialize intl instance:", error)
      // Fallback to a basic instance to prevent complete failure
      this.intlInstance = {
        formatMessage: ({ id }) => {
          // eslint-disable-next-line no-console
          console.warn(`IntlStore: Using fallback for message "${id}"`)
          return id
        } // Just return the ID as fallback
      }
    }
  }
  formatMessage = (id: string, extra?: Object) => {
    // Prevent infinite loops with a formatting flag
    if (this._isFormatting) {
      console.warn(`IntlStore: Preventing recursive formatting for "${id}"`)
      return id
    }

    this._isFormatting = true

    try {
      if (!this.intlInstance || !this.intlInstance.formatMessage) {
        console.warn(
          "IntlStore: intl instance not properly initialized, reinitializing..."
        )
        this.setInstance()
      }

      // Double-check that we have the message before trying to format it
      if (!this.messages || !this.messages[id]) {
        console.warn(
          `IntlStore: Message "${id}" not found in current locale messages`
        )
        return id
      }

      // For the problematic message, let's try a direct approach first
      if (id === "apps.driverWebStore.error.NOT_FOUND") {
        const messageValue = this.messages[id]
        console.log(
          `IntlStore: Direct message lookup for "${id}":`,
          messageValue
        )

        // If it's a simple string, return it directly to avoid react-intl issues
        if (typeof messageValue === "string") {
          return messageValue
        }
      }

      const result = this.intlInstance.formatMessage({ id }, extra)
      return result
    } catch (error) {
      console.error(`IntlStore: Error formatting message "${id}":`, error)
      console.error("IntlStore: Current locale:", this.locale)
      console.error("IntlStore: Message exists:", !!this.messages?.[id])
      console.error("IntlStore: Message value:", this.messages?.[id])
      console.error("IntlStore: Message type:", typeof this.messages?.[id])

      // For any error, just return the raw message or the ID
      const rawMessage = this.messages?.[id]
      if (typeof rawMessage === "string") {
        console.warn(`IntlStore: Returning raw message for "${id}"`)
        return rawMessage
      }

      // Final fallback to the ID itself if all formatting fails
      return id
    } finally {
      this._isFormatting = false
    }
  }

  @action
  setSelectedLanguage = (language: Language) => {
    UiStore.setIntlLanguage(language)
    this.locale = locales[language]
    this.setInstance()
  }
}

export default (new IntlStore(): any)
